/* eslint-disable camelcase */
/* eslint-disable react/no-danger */
import getConfig from "next/config";
import Script from "next/script";

import { useAppState } from "hooks/AppContext";
import {
    AddressComponent, GeocodedLocation, JobLocation, SingleCompany, SingleJob
} from "interfaces/api/apiInterfaces";
import { getCompanyLogo } from "utils/strings";
import { locationIsRemote } from "utils/location";

interface JSONLocation {
    addressCountry?: string;
    addressLocality?: string;
    addressRegion?: string;
    postalCode?: string;
    route?: string;
    streetAddress?: string;
}

/**
 * Construct a job url.
 */
const getJobUrl = (companyShortName: string, jobShortname: string) => {
    const { publicRuntimeConfig: { appEnv } } = getConfig();
    const path = `/jobs/${companyShortName}/${jobShortname}`;
    return (
        appEnv === "production"
            ? `https://www.themuse.com${path}`
            : `https://www.staging-themuse.com${path}`
    );
};

/**
 * Retrieve the parts of the location from the geocoded properties of an address_components
 */
const getParts = (item: AddressComponent) => {
    if (item?.types?.includes("street_number")) return { streetAddress: item.long_name };
    if (item?.types?.includes("route")) return { route: item.long_name };
    if (item?.types?.includes("locality")) return { addressLocality: item.long_name };
    if (item?.types?.includes("postal_code")) return { postalCode: item.short_name };
    if (item?.types?.includes("administrative_area_level_1")) return { addressRegion: item.short_name };
    if (item?.types?.includes("country")) return { addressCountry: item.short_name };
    return {};
};

/**
 * Generates the JSON address object with whatever keys we can find.
 */
const getJSONFromGeocodedLocation = (location: JobLocation) => {
    const geocodedLocations = Array.isArray(location?.geocoded_locations)
        ? location.geocoded_locations
        : [location?.geocoded_locations];

    const stringifiedGeo = geocodedLocations
    // Lets loop over all those locations.
        ?.map((geo: GeocodedLocation) => {
        // Now we loop again over the addresses
            const result: JSONLocation = geo?.address_components
            // Map with getParts to generate all the correctly named JSON properties.
                ?.map(getParts)
            // Reduce all those objects into a single object.
                ?.reduce((a, b) => ({ ...a, ...b }), {});
            // Append the json type.
            if (result) {
                result["@type"] = "PostalAddress";
                // Combine the street address and route into a full local address.
                if (result?.streetAddress && result?.route) {
                    result.streetAddress = `${result.streetAddress} ${result.route}`;
                    delete result.route;
                }

                // Remove all the empty/falsy properties.
                Object.keys(result).forEach((key) => {
                    if (!result[key]) {
                        delete result[key];
                    }
                });
            }
            return result
                ? { "@type": "Place", address: result }
                : {};
        })
        // Reduce to a single array.
        .flat()
        // Remove empty objects or ones that only have the @type.
        .filter((x) => Object.keys(x).length > 1)
        // Remove null and undefined entries
        .filter(Boolean)
        .map((x) => JSON.stringify(x));

    return stringifiedGeo?.length
        // Create a map so we can remove duplicates.
        ? [...new Set(stringifiedGeo)].map((x) => JSON.parse(x))
        : [];
};

/**
 * Generate a JSON address format for locations.
 */
const getMuseLocations = (locations: JobLocation[]) => (
    locations
        .filter((location) => !locationIsRemote(location))
        .map((location) => (
            {
                "@type": "Place",
                address: {
                    "@type": "PostalAddress",
                    addressLocality: location?.city,
                    addressCountry: location?.country,
                    addressRegion: location?.state
                }
            }
        ))
);

/**
 * Filter for actual locations and format them for consumption by google.
 */
const getJobLocations = (locations: JobLocation[]) => {
    const geoCodedLocations = locations
        .filter((location) => !locationIsRemote(location))
        .map(getJSONFromGeocodedLocation)
        .flat();

    return geoCodedLocations.length < 1 ? getMuseLocations(locations) : geoCodedLocations;
};

interface JobPostingJsonInterface {
    company: SingleCompany;
    job: SingleJob;
    isDirectApply?: boolean
}

/**
 * Convert the perks and benifits object into a single comma delimited string of perk names.
 */
const getBenefitsString = (perkList: Record<string, any>): string => Object
    .keys(perkList)
    .map((group: string) => perkList[group]
        .perks
        .map((perk: Record<string, any>) => perk.name)
        .filter(Boolean)
        .join(","))
    .filter(Boolean)
    .join(", ");

const generateJobPostingJson = (props: JobPostingJsonInterface): Record<string, any> => {
    const { company, job, isDirectApply } = props;
    const {
        perks_and_benefits,
        name: companyName,
        logo,
        short_name: companyShortName
    } = company;
    const {
        locations,
        posted_at: datePosted,
        compiled_contents,
        name: title,
        valid_through: validThrough,
        short_name: jobShortName,
        ats_info
    } = job;

    const jobLocation = getJobLocations(locations);
    const isFlexibleRemote = locations?.find((location) => locationIsRemote(location));
    const jobBenefits = getBenefitsString(perks_and_benefits);
    const jsonld = {
        "@context": "https://schema.org",
        "@type": "JobPosting",
        datePosted,
        description: compiled_contents.join(""),
        experienceRequirements: "no requirements",
        hiringOrganization: {
            "@type": "Organization",
            name: companyName,
            logo: getCompanyLogo(logo)
        },
        ...(isDirectApply && { directApply: true }),
        ...(jobLocation?.length && { jobLocation }),
        ...(isFlexibleRemote && {
            jobLocationType: "TELECOMMUTE",
            applicantLocationRequirements: {
                "@type": "Country",
                sameAs: "https://www.wikidata.org/wiki/Q30",
                name: "USA"
            }
        }),
        title,
        employmentType: "OTHER",
        url: getJobUrl(companyShortName, jobShortName),
        ...(validThrough && { validThrough }),
        identifier: {
            "@type": "PropertyValue",
            name: companyName,
            value: ats_info?.id
        },
        ...(jobBenefits && { jobBenefits })
    };

    return jsonld;
};

const JobPostJsonLd = (props: { isDirectApply?: boolean }) => {
    const { company, job } = useAppState();
    const { isDirectApply } = props;
    const jobLocation = getJobLocations(job?.locations);
    const isFlexibleRemote = job?.locations?.find((location) => locationIsRemote(location));
    const hasValidLocation = isFlexibleRemote || jobLocation?.length;

    if (!hasValidLocation || job?.state !== "live") {
        return null;
    }

    const jsonld = generateJobPostingJson({ company, job, isDirectApply });

    return (
        <Script
            strategy="beforeInteractive"
            id="JobPost-JSON"
            key="JobPost-JSON"
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonld) }}
        />
    );
};

// WARNING: This is for unit testing only and should NEVER be used directly outside this file.
const generateJobPostingJsonTestingOnly = generateJobPostingJson;

export {
    JobPostJsonLd,
    generateJobPostingJsonTestingOnly,
};
